import { Card, CardContent, CardDescription, CardFooter, CardTitle } from '@1trading/react/components/ui/card';
import { cn } from '@1trading/react/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import React, { createContext, useContext } from 'react';

const PageCardTitle = CardTitle;

const PageCardDescription = CardDescription;

const pageCardVariants = cva('-md:border-none -md:rounded-none -md:shadow-none', {
  variants: {
    fixedFooter: {
      true: 'has-[[data-fixed-footer]]:[--page-card-fixed-footer-height:5.5rem]',
      false: '',
    },
  },
  defaultVariants: {
    fixedFooter: false,
  },
});

type PageCardVariants = VariantProps<typeof pageCardVariants>;

const PageCardContext = createContext<Partial<Pick<PageCardVariants, 'fixedFooter'>>>({ fixedFooter: false });

type PageCardProps = React.ComponentPropsWithoutRef<typeof Card> & PageCardVariants;

const PageCard = React.forwardRef<React.ElementRef<typeof Card>, PageCardProps>(({ children, className, fixedFooter, ...props }, ref) => {
  return (
    <PageCardContext.Provider value={{ fixedFooter }}>
      <Card ref={ref} className={cn(pageCardVariants({ fixedFooter }), className)} {...props}>
        {children}
      </Card>
    </PageCardContext.Provider>
  );
});
PageCard.displayName = 'PageCard';

const pageCardContentVariants = cva('-md:pt-0 -md:px-0', {
  variants: {
    fixedFooter: {
      true: '-md:pb-[var(--page-card-fixed-footer-height)]',
      false: '',
    },
  },
  defaultVariants: {
    fixedFooter: false,
  },
});

const PageCardContent = React.forwardRef<React.ElementRef<typeof CardContent>, React.ComponentPropsWithoutRef<typeof CardContent>>(({ children, className, ...props }, ref) => {
  const { fixedFooter } = useContext(PageCardContext);

  return (
    <CardContent ref={ref} className={cn(pageCardContentVariants({ fixedFooter }), className)} {...props}>
      {children}
    </CardContent>
  );
});
PageCardContent.displayName = 'PageCardContent';

const pageCardFooterVariants = cva('-md:p-0', {
  variants: {
    fixedFooter: {
      true: '-md:fixed -md:bottom-0 -md:left-0 -md:right-0 -md:flex -md:items-center -md:justify-stretch -md:h-[var(--page-card-fixed-footer-height)] -md:w-full -md:border-t -md:dark:border-border/20 -md:p-4 -md:bg-card',
      false: '',
    },
  },
  defaultVariants: {
    fixedFooter: false,
  },
});

const PageCardFooter = React.forwardRef<React.ElementRef<typeof CardFooter>, React.ComponentPropsWithoutRef<typeof CardFooter>>(({ children, className, ...props }, ref) => {
  const { fixedFooter } = useContext(PageCardContext);

  return (
    <CardFooter ref={ref} className={cn(pageCardFooterVariants({ fixedFooter }), className)} {...props} {...(fixedFooter ? { 'data-fixed-footer': '' } : '')}>
      {children}
    </CardFooter>
  );
});
PageCardFooter.displayName = 'PageCardFooter';

export { PageCard, PageCardContent, PageCardDescription, PageCardFooter, PageCardTitle };
